import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "../../services/api";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "./styles/AnniversaryList.css";

function AnniversaryList() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;

  const [search, setSearch] = useState({
    firstName: "",
    lastName: "",
    fatherName: ""
  });
  const [submittedSearch, setSubmittedSearch] = useState(search);
  const [showFilters, setShowFilters] = useState(false);

  const observer = useRef();
  const lastProfileRef = useCallback((node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    }, { rootMargin: "100px", threshold: 0.1 });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const formatShortDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }, []);

  const calculateAge = useCallback((birth, death) => {
    const dob = new Date(birth);
    const dod = new Date(death);
    let age = dod.getFullYear() - dob.getFullYear();
    const m = dod.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && dod.getDate() < dob.getDate())) age--;
    return age;
  }, []);

  const calculateYearsSinceDeath = useCallback((deathDate) => {
    const now = new Date();
    const death = new Date(deathDate);
    return now.getFullYear() - death.getFullYear();
  }, []);

  const capitalizeWords = useCallback((text) => {
    if (!text) return "";
    return text.toLowerCase().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/memorialprofile/anniversaries", {
        params: { ...submittedSearch, page, pageSize }
      });
      const newProfiles = res.data.data || [];
      setProfiles(prev => page === 1 ? newProfiles : [...prev, ...newProfiles]);
      setHasMore(newProfiles.length === pageSize);
    } catch (err) {
      console.error("İl dönümü profilləri yüklənmədi:", err);
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, page, pageSize]);

  useEffect(() => {
    loadProfiles();
  }, [page, submittedSearch]);

  const handleSearchChange = useCallback((e) => {
    const { name, value } = e.target;
    setSearch(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    setProfiles([]);
    setPage(1);
    setHasMore(true);
    setSubmittedSearch(search);
  }, [search]);

  const hasActiveSearch = search.firstName || search.lastName || search.fatherName;

  const profileCards = useMemo(() => {
    return profiles.map((profile, idx) => {
      const isLast = profiles.length === idx + 1;
      const age = calculateAge(profile.dateOfBirth, profile.dateOfDeath);
      const yearsSince = calculateYearsSinceDeath(profile.dateOfDeath);

      return (
        <Link
          to={`/merhum/${profile.id}`}
          key={profile.id}
          className={`av-card ${profile.isMartyr ? "av-card--martyr" : ""}`}
          ref={isLast ? lastProfileRef : null}
          style={{ '--delay': `${(idx % 12) * 0.05}s` }}
        >
          <div className="av-card__img">
            <img
              src={profile.photoUrl ? `https://api.heyatyolu.az${profile.photoUrl}` : "/placeholder.jpg"}
              alt={`${profile.firstName} ${profile.lastName}`}
              loading="lazy"
              onError={(e) => { e.target.src = "/placeholder.jpg"; }}
            />
            <div className="av-card__year-badge">
              <span className="av-card__year-num">{yearsSince}</span>
              <span className="av-card__year-label">il</span>
            </div>
            {profile.isMartyr && (
              <div className="av-card__martyr-flag">🇦🇿</div>
            )}
          </div>

          <div className="av-card__body">
            <h3 className="av-card__name">
              {capitalizeWords(`${profile.firstName} ${profile.fatherName} ${profile.lastName}`)}
            </h3>

            <div className="av-card__dates">
              <span>{formatShortDate(profile.dateOfBirth)}</span>
              <span className="av-card__age">{age}</span>
              <span>{formatShortDate(profile.dateOfDeath)}</span>
            </div>

            {profile.birthPlace && (
              <p className="av-card__location">
                <i className="fas fa-map-marker-alt"></i> {profile.birthPlace}
              </p>
            )}

            <div className="av-card__footer">
              <span className="av-card__anniversary">
                🕯️ {yearsSince}-ci il dönümü
              </span>
            </div>
          </div>
        </Link>
      );
    });
  }, [profiles, lastProfileRef, capitalizeWords, formatShortDate, calculateAge, calculateYearsSinceDeath]);

  useEffect(() => {
    return () => { if (observer.current) observer.current.disconnect(); };
  }, []);

  const isFirstLoad = page === 1 && profiles.length === 0 && loading;

  // Bugünkü tarixi göstər
  const todayFormatted = useMemo(() => {
    const now = new Date();
    const months = [
      "yanvar", "fevral", "mart", "aprel", "may", "iyun",
      "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"
    ];
    return `${now.getDate()} ${months[now.getMonth()]}`;
  }, []);

  return (
    <div className="av-page">
      <Helmet>
        <title>İl Dönümü Xatirələri – HeyatYolu</title>
        <meta name="description" content="Bu gün həyatını itirən sevdiklərimizin il dönümünü yad edək. Xatirələrə bax və paylaş." />
        <meta name="keywords" content="il dönümü, xatirə, mərhum, şəhid, yaddaş, vəfat, rəqəmsal yas, heyatyolu" />
        <meta property="og:title" content="İl Dönümü Xatirələri – HeyatYolu" />
        <meta property="og:description" content="Bu gün il dönümü olan mərhumların xatirələrini paylaş." />
        <meta property="og:image" content="https://api.heyatyolu.az/favicon.ico" />
        <meta property="og:url" content="https://api.heyatyolu.az/il-donumu" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* HEADER */}
      <header className="av-header">
        <div className="av-header__candles">
          <span>🕯️</span><span>🕯️</span><span>🕯️</span>
        </div>
        <div className="av-header__inner">
          <p className="av-header__date">{todayFormatted}</p>
          <h1 className="av-header__title">İl Dönümü Xatirələri</h1>
          <p className="av-header__sub">
            Bu gün vəfatlarının il dönümü olan əzizlərimizi hörmətlə yad edirik
          </p>
        </div>
      </header>

      {/* SEARCH */}
      <section className="av-toolbar">
        <form onSubmit={handleSearch} className="av-search">
          <div className="av-search__main">
            <i className="fas fa-search av-search__icon"></i>
            <input
              type="text"
              name="firstName"
              placeholder="Ad ilə axtar..."
              value={search.firstName}
              onChange={handleSearchChange}
              className="av-search__input"
            />
            <button
              type="button"
              className={`av-search__toggle ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              title="Ətraflı axtarış"
            >
              <i className="fas fa-sliders-h"></i>
            </button>
            <button type="submit" className="av-search__btn" disabled={loading}>
              {loading ? <i className="fas fa-spinner fa-spin"></i> : "Axtar"}
            </button>
          </div>

          {showFilters && (
            <div className="av-search__expanded">
              <input
                type="text"
                name="fatherName"
                placeholder="Ata adı..."
                value={search.fatherName}
                onChange={handleSearchChange}
              />
              <input
                type="text"
                name="lastName"
                placeholder="Soyad..."
                value={search.lastName}
                onChange={handleSearchChange}
              />
            </div>
          )}
        </form>

        {hasActiveSearch && (
          <div className="av-active-filters">
            <span className="av-chip">
              Axtarış aktiv
              <button onClick={() => {
                setSearch({ firstName: "", lastName: "", fatherName: "" });
                setSubmittedSearch({ firstName: "", lastName: "", fatherName: "" });
                setProfiles([]);
                setPage(1);
                setHasMore(true);
              }}>×</button>
            </span>
          </div>
        )}
      </section>

      {/* RESULTS */}
      <section className="av-results">
        {isFirstLoad ? (
          <div className="av-grid">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="av-skeleton">
                <div className="av-skeleton__img"></div>
                <div className="av-skeleton__body">
                  <div className="av-skeleton__line av-skeleton__line--long"></div>
                  <div className="av-skeleton__line av-skeleton__line--medium"></div>
                  <div className="av-skeleton__line av-skeleton__line--short"></div>
                </div>
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="av-empty">
            <div className="av-empty__icon">🕯️</div>
            <h3>Bu gün üçün il dönümü qeyd olunmayıb</h3>
            <p>Başqa adlarla axtarmağı yoxlayın</p>
          </div>
        ) : (
          <div className="av-grid">
            {profileCards}
          </div>
        )}
      </section>

      {/* LOADING MORE */}
      {loading && !isFirstLoad && (
        <div className="av-loading-more">
          <div className="av-loading-more__spinner"></div>
          <span>Daha çox yüklənir...</span>
        </div>
      )}

      {/* END */}
      {profiles.length > 0 && !hasMore && (
        <div className="av-end">
          <span className="av-end__icon">🕯️</span>
          <p>Ruhları şad olsun</p>
        </div>
      )}
    </div>
  );
}

export default AnniversaryList;